// GW-445 — "Coming Soon" badge + price hide on the Product Compare page.
//
// The compare page is rendered by OE / the Goldwagen theme and knows nothing about
// our coming-soon status, so a coming-soon product shows its R0.00 price. This
// global widget reads each compared product's SKU (on the `.remove-from-compare
// [data-product-sku]` button), asks the API which are coming-soon (one bulk call,
// same endpoint the wishlist widget uses), then on those products: overlays a navy
// "Coming Soon" ribbon on the image and replaces the R0.00 price with a muted "—".
//
// Handles BOTH the Goldwagen theme (desktop `.cmp-*` table + mobile `.cmp-m-card`
// cards) AND the stock `.product-box-*` markup — it matches product columns/cards
// by the SKU/price ELEMENTS present, not by fixed class names, so it degrades
// gracefully across themes. Pure client-side; no-ops if the markup differs.

import { apiJson } from '../shared/api.js';
import { getTopDocument } from '../shared/dom.js';

export var id = 'compare-coming-soon';

var PRICE_SEL = '.cmp-price,.cmp-m-price,.product-box-price';

function makeBadge(doc) {
  var span = doc.createElement('span');
  span.className = 'pf-compare-cs-badge';
  span.setAttribute('aria-label', 'Coming soon');
  span.innerHTML = '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">'
    + '<circle cx="12" cy="12" r="9"></circle><path stroke-linecap="round" stroke-linejoin="round" d="M12 7v5l3 2"></path></svg>Coming Soon';
  return span;
}

function badgeImg(doc, container) {
  var img = container && container.querySelector ? container.querySelector('.product-box-img') : null;
  if (img && !img.querySelector('.pf-compare-cs-badge')) {
    img.classList.add('pf-compare-cs-img');
    img.appendChild(makeBadge(doc));
  }
}

function hidePrice(el) {
  if (!el) return;
  el.textContent = '-';
  el.classList.add('pf-compare-cs-price');
}

function toArray(nodeList) { return Array.prototype.slice.call(nodeList || []); }

export function init() {
  var doc = getTopDocument();
  if (!doc.querySelector('.compare-contain') && !doc.querySelector('.product-compare-contain') && !doc.querySelector('.remove-from-compare')) return;

  var removeBtns = doc.querySelectorAll('.remove-from-compare[data-product-sku]');
  if (!removeBtns.length) return;

  var skuSet = {};
  var skus = [];
  toArray(removeBtns).forEach(function (b) {
    var s = (b.getAttribute('data-product-sku') || '').trim();
    if (s && !skuSet[s.toUpperCase()]) { skuSet[s.toUpperCase()] = true; skus.push(s); }
  });
  if (!skus.length) return;

  apiJson('/api/parts/coming-soon?skus=' + encodeURIComponent(skus.join(',')), null, { comingSoon: [] })
    .then(function (res) {
      var list = (res && res.comingSoon) || [];
      if (list.length === 0) return;
      var cs = {};
      list.forEach(function (s) { cs[String(s).toUpperCase()] = true; });
      var isCS = function (sku) { return !!(sku && cs[sku.trim().toUpperCase()]); };

      // ── Mobile cards: SKU, image and price all live in the same card. ──
      toArray(doc.querySelectorAll('.cmp-m-card')).forEach(function (card) {
        var b = card.querySelector('.remove-from-compare[data-product-sku]');
        if (!b || !isCS(b.getAttribute('data-product-sku'))) return;
        badgeImg(doc, card);
        hidePrice(card.querySelector(PRICE_SEL));
      });

      // ── Desktop table: image and price are in DIFFERENT rows, same column ──
      // index. Find the image row + price row, then zip their PRODUCT cells (the
      // ones carrying a remove button / a price element) by position.
      var firstImg = toArray(doc.querySelectorAll('.product-box-img')).filter(function (i) { return !(i.closest && i.closest('.cmp-m-card')); })[0];
      var imgRow = firstImg && firstImg.closest ? firstImg.closest('tr') : null;
      var firstPrice = toArray(doc.querySelectorAll(PRICE_SEL)).filter(function (p) { return !(p.closest && p.closest('.cmp-m-card')); })[0];
      var priceRow = firstPrice && firstPrice.closest ? firstPrice.closest('tr') : null;
      if (imgRow) {
        var imgCells = toArray(imgRow.querySelectorAll('td')).filter(function (td) { return td.querySelector('.remove-from-compare[data-product-sku]'); });
        var priceCells = priceRow ? toArray(priceRow.querySelectorAll('td')).filter(function (td) { return td.querySelector(PRICE_SEL); }) : [];
        imgCells.forEach(function (cell, i) {
          var b = cell.querySelector('.remove-from-compare[data-product-sku]');
          if (!b || !isCS(b.getAttribute('data-product-sku'))) return;
          badgeImg(doc, cell);
          if (priceCells[i]) hidePrice(priceCells[i].querySelector(PRICE_SEL));
        });
      }
    })
    .catch(function () { /* leave the page as-is on error */ });
}
